import {Localized, useLocalization} from "@fluent/react";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import EditIcon from "@mui/icons-material/Edit";
import ErrorIcon from "@mui/icons-material/Error";
import {
	Box,
	Button,
	Divider,
	Grid2 as Grid,
	Link,
	Paper,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableHead,
	TextField,
	Typography,
} from "@mui/material";
import React, {useContext, useEffect, useState} from "react";

import ContentItemTitle from "../analytics/ContentItemTitle";
import ProgressTableChapterRow from "../analytics/ProgressTableChapterRow";
import ProgressTableExerciseRow from "../analytics/ProgressTableExerciseRow";
import ProgressTableSections from "../analytics/ProgressTableSections";
import AssessmentPageDialogContext from "./AssessmentPageDialogContext";
import {resolveLinkToOldSite} from "../../helpers/pathHelpers";
import selectChapter from "../../store/chapters/selectChapter";
import selectCourseChapterKeys from "../../store/chapters/selectCourseChapterKeys";
import testSettingsOverriden from "../../store/chapterSettingsOverrides/testSettingsOverriden";
import CourseType from "../../store/courses/CourseType";
import selectCourse from "../../store/courses/selectCourse";
import type ChapterProgress from "../../store/courseProgress/ChapterProgress";
import type ExerciseOutcomes from "../../store/courseProgress/ExerciseOutcomes";
import selectCourseStats from "../../store/courseStatistics/selectCourseStats";
import ExerciseType from "../../store/exercises/ExerciseType";
import type FetchStatus from "../../store/FetchStatus";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {keyProvider} from "../../store/keyProvider";
import type {
	ProgressByChapter,
	ProgressByType,
	StudentResultsInCourse,
} from "../../store/services/analyticsService";
import {analyticsService} from "../../store/services/analyticsService";
import {courseService as assessmentAdministrationService} from "../../store/services/courseService";
import type {ChapterExercise} from "../../store/services/dtos/ChapterContent";
import useSnackbar from "../../store/ui/useSnackbar";
import {selectUserId} from "../../store/userProfile/selectUserProfile";
import StudentResultsRow from "./StudentResultsRow";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";
import ProgressButton from "../../utils/ProgressButton";

const StudentProgressInChapters = (props: {
	student: StudentResultsInCourse;
	studentProgressInChapters: ProgressByChapter[];
	courseId: number;
	barType: "stacked" | "plain";
	viewOnly?: boolean;
	onCollapse: () => void;
}): JSX.Element => {
	const {courseId, student, barType, studentProgressInChapters} = props;

	const courseStats = useAppSelector((state) =>
		selectCourseStats(state, keyProvider.course(courseId))
	);

	const [progress, setProgress] = useState<{
		[key: string]: ChapterProgress;
	}>({});

	const [loading, setLoading] = useState(true);

	const studentId = student.id;

	useEffect(() => {
		if (!studentId) {
			return;
		}

		setLoading(true);

		analyticsService
			.getStudentCourseProgress(studentId, courseId)
			.then((res) => {
				const byChapters: {[key: string]: ChapterProgress} = {};

				res.forEach((p) => {
					const key = keyProvider.chapter(p.chapterId);
					byChapters[key] = p;
				});

				setProgress(byChapters);
			})
			.finally(() => setLoading(false));
	}, [courseId, studentId]);

	if (!courseStats) {
		return <></>;
	}

	return (
		<Table stickyHeader sx={{pb: 3}}>
			<TableHead>
				<StudentResultsRow
					courseId={courseId}
					courseStats={courseStats}
					student={student}
					progressByTypeInChapters={studentProgressInChapters}
					actionIcon={<FullscreenExitIcon />}
					onAction={props.onCollapse}
					barType={barType}
					loading={loading}
				/>
			</TableHead>
			<TableBody>
				<ProgressRows
					courseId={courseId}
					studentId={student.id}
					loading={loading}
					progress={progress}
					progressByType={studentProgressInChapters.reduce(
						(prev, curr) => ({
							...prev,
							[keyProvider.chapter(curr.chapterId)]: curr.progress,
						}),
						{}
					)}
					barType={barType}
					viewOnly={props.viewOnly}
				/>
			</TableBody>
		</Table>
	);
};

function ProgressRows(props: {
	courseId: number;
	studentId: number;
	loading: boolean;
	progress: {[key: string]: ChapterProgress | undefined};
	progressByType: {[key: string]: ProgressByType[] | undefined};
	barType: "stacked" | "plain";
	viewOnly?: boolean;
}) {
	const {courseId, studentId, loading, progress, progressByType} = props;

	const chapters = useAppSelector((state) =>
		selectCourseChapterKeys(state, keyProvider.course(courseId))
	);

	return (
		<>
			{chapters.map((key) => (
				<ProgressTableChapterRow
					alwaysRenderChapterContent
					studentId={studentId}
					chapterKey={key}
					chapterProgress={progress[key] ?? null}
					loading={loading}
					key={key}
					bar={
						props.barType === "stacked"
							? {type: "stacked", progressByType: progressByType[key] ?? []}
							: {type: "plain"}
					}
					renderTitle={(chapter) => (
						<ContentItemTitle
							name={chapter.title}
							number={`${chapter.number}`}
						/>
					)}
					renderSectionTable={(
						chapterId,
						_,
						chapterProgress,
						sessionRequired
					) => (
						<ChapterContent
							studentId={studentId}
							chapterId={chapterId}
							chapterProgress={chapterProgress}
							sessionRequired={sessionRequired}
							viewOnly={props.viewOnly}
						/>
					)}
				/>
			))}
		</>
	);
}

function ChapterContent(props: {
	studentId: number;
	chapterId: number;
	chapterProgress: ChapterProgress;
	sessionRequired?: boolean;
	viewOnly?: boolean;
}) {
	const {chapterId, studentId} = props;

	const chapter = useAppSelector((state) =>
		selectChapter(state, keyProvider.chapter(chapterId))
	);

	const courseType = useAppSelector(
		(state) =>
			chapter?.courseId &&
			selectCourse(state, keyProvider.course(chapter.courseId))?.type
	);

	const testSettingsOverridable =
		Boolean(props.sessionRequired) && courseType === CourseType.Ordinary;

	const progressShowable =
		!props.sessionRequired || Boolean(props.chapterProgress.startTime);

	if (!chapter || !courseType) {
		return <></>;
	}

	if (chapter.structureType !== "sections") {
		return <ChapterHasOldStructureWarning courseId={chapter.courseId} />;
	}

	return (
		<>
			{testSettingsOverridable && (
				<TestSettings
					studentId={studentId}
					courseId={chapter.courseId}
					chapterId={chapterId}
					sessionLimit={chapter.sessionLimit}
				/>
			)}

			{progressShowable && (
				<ProgressTableSections
					studentId={studentId}
					chapterId={chapter.id}
					courseId={chapter.courseId}
					chapterProgress={props.chapterProgress}
					sessionRequired={props.sessionRequired}
					renderExerciseRow={(section, exercise, outcomes) => (
						<ExerciseRow
							studentId={studentId}
							exercise={exercise}
							sectionNumber={section.number}
							outcomes={outcomes}
							key={exercise.id}
							viewOnly={props.viewOnly}
						/>
					)}
					renderTitle={(sectionNumber) =>
						props.sessionRequired &&
						sectionNumber > 0 && (
							<Typography
								variant="subtitle2"
								sx={{
									pl: 2.75,
									height: (theme) => `calc(1.5rem + ${theme.spacing(1)})`,
									display: "flex",
									alignItems: "center",
								}}
							>
								<Localized id="follow-up-student-results-widget-test-results">
									Test results
								</Localized>
							</Typography>
						)
					}
				/>
			)}
		</>
	);
}

function ChapterHasOldStructureWarning(props: {courseId: number}) {
	const link = resolveLinkToOldSite(`teacher/${props.courseId}/#/followup`);

	return (
		<Box display="flex" flexDirection="column" alignItems="center" py={1}>
			<Typography variant="subtitle2">
				<Localized id="follow-up-student-results-widget-old-chapter-warning-title">
					Chapter has old structure
				</Localized>
			</Typography>
			<Typography variant="body2">
				<Localized
					id="follow-up-student-results-widget-old-chapter-warning-link"
					elems={{ref: <Link href={link}></Link>}}
				>
					<>{"You can find student results in <ref>the old interface</ref>"}</>
				</Localized>
			</Typography>
		</Box>
	);
}

function ExerciseRow(props: {
	studentId: number;
	exercise: ChapterExercise;
	sectionNumber: number;
	outcomes?: ExerciseOutcomes;
	viewOnly?: boolean;
}) {
	const {outcomes, exercise} = props;

	const {openDialog} = useContext(AssessmentPageDialogContext);

	const title = (
		<ContentItemTitle
			number={`${props.sectionNumber}.${exercise.number}`}
			name={exercise.title}
		/>
	);

	return (
		<ProgressTableExerciseRow
			outcomes={outcomes}
			maxScore={exercise.maxScore ?? 0}
			title={
				!props.viewOnly &&
				outcomes &&
				exercise.type !== ExerciseType.External &&
				exercise.type !== ExerciseType.Short ? (
					<Link
						color="inherit"
						style={{cursor: "pointer"}}
						onClick={() =>
							openDialog({
								chapterId: exercise.chapterId,
								exerciseId: exercise.id,
								exerciseType: exercise.type,
								studentId: props.studentId,
							})
						}
					>
						{title}
					</Link>
				) : (
					title
				)
			}
		/>
	);
}

function TestSettings(props: {
	studentId: number;
	courseId: number;
	chapterId: number;
	sessionLimit?: number;
}) {
	const {studentId, courseId, chapterId} = props;

	const dispatch = useAppDispatch();
	const showSnackbar = useSnackbar();
	const {l10n} = useLocalization();

	const userId = useAppSelector(selectUserId);

	const [editorOpen, setEditorOpen] = useState(false);

	const [settings, setSettings] = useState<{
		sessionLimitExtension?: number;
	}>({});

	const [settingsFetchStatus, setSettingsFetchStatus] = useState<FetchStatus>(
		"none"
	);

	function fetchTestSettingsOverride(
		studentId: number,
		courseId: number,
		chapterId: number
	) {
		setSettingsFetchStatus("pending");

		assessmentAdministrationService
			.getTestSettingsOverride(studentId, courseId, chapterId)
			.then((s) => {
				setSettings(s);
				setSettingsFetchStatus("succeeded");
			})
			.catch(() => {
				setSettingsFetchStatus("failed");
			});
	}

	useEffect(() => {
		fetchTestSettingsOverride(studentId, courseId, chapterId);
	}, [chapterId, courseId, studentId]);

	async function saveSettings(settings: {sessionLimit: number}) {
		try {
			await assessmentAdministrationService.overrideTestSettings(
				studentId,
				courseId,
				chapterId,
				{
					sessionLimitExtension: settings.sessionLimit || null,
				}
			);

			if (studentId === userId) {
				dispatch(testSettingsOverriden({chapterId}));
			}

			setSettings({sessionLimitExtension: settings.sessionLimit});

			return true;
		} catch {
			showSnackbar("error", l10n.getString("error-general"));
			return false;
		}
	}

	const sessionLimit = props.sessionLimit ?? 0;
	const sessionLimitExtension = settings.sessionLimitExtension ?? 0;

	if (editorOpen) {
		return (
			<Stack sx={{pl: 2.75, mb: 1}}>
				<TestSettingsEditor
					initialSessionLimit={sessionLimit}
					initialSessionLimitExtra={sessionLimitExtension}
					onSave={saveSettings}
					onClose={() => setEditorOpen(false)}
				/>
			</Stack>
		);
	}

	return (
		<Stack
			sx={[
				{pl: 2.75, pb: 2, mb: 1},
				settingsFetchStatus !== "failed" && {
					"&:hover": {
						background: (theme) => theme.palette.action.hover,
					},
				},
			]}
		>
			<Stack
				direction="row"
				sx={{justifyContent: "space-between", alignItems: "center"}}
			>
				<Typography variant="subtitle2">
					<Localized id="follow-up-student-results-widget-test-settings">
						Test settings
					</Localized>
				</Typography>
				<IconButtonWithTooltip
					tooltipTitle={
						<Localized id="follow-up-student-results-widget-test-settings-action-edit">
							Edit
						</Localized>
					}
					aria-label={l10n.getString(
						"follow-up-student-results-widget-test-settings-action-edit"
					)}
					disabled={settingsFetchStatus === "pending"}
					onClick={() => setEditorOpen(true)}
					sx={{
						mt: 1,
						mr: 1,
						visibility: settingsFetchStatus === "failed" ? "hidden" : "inherit",
					}}
				>
					<EditIcon />
				</IconButtonWithTooltip>
			</Stack>

			{settingsFetchStatus === "failed" && (
				<TestSettingsFetchErrorState
					onReload={() =>
						fetchTestSettingsOverride(studentId, courseId, chapterId)
					}
				/>
			)}

			{settingsFetchStatus !== "failed" && (
				<Stack>
					<SettingsRow
						loading={settingsFetchStatus === "pending"}
						description={
							<Localized id="follow-up-student-results-widget-test-settings-label-attempts">
								Number of attempts:
							</Localized>
						}
						value={
							<>
								{Boolean(sessionLimit) && !sessionLimitExtension && (
									<>{sessionLimit}</>
								)}

								{Boolean(sessionLimit) && Boolean(sessionLimitExtension) && (
									<Localized
										id="follow-up-student-results-widget-test-settings-attempts-number-with-extra"
										vars={{
											limit: sessionLimit + sessionLimitExtension,
											extra: sessionLimitExtension,
										}}
									>
										{`${sessionLimit} (additional ${sessionLimitExtension})`}
									</Localized>
								)}

								{!sessionLimit && (
									<em>
										<Localized id="follow-up-student-results-widget-test-settings-attempts-number-no-limit">
											unlimited
										</Localized>
									</em>
								)}
							</>
						}
					/>
				</Stack>
			)}
		</Stack>
	);
}

function TestSettingsFetchErrorState(props: {onReload: () => void}) {
	return (
		<Stack
			direction="row"
			sx={{flexWrap: "wrap", alignItems: "center", pl: "1.5rem"}}
		>
			<Stack
				direction="row"
				spacing={1}
				sx={{alignItems: "flex-start", ml: "-1.5rem", mr: 1}}
			>
				<ErrorIcon color="error" />
				<Typography align="justify">
					<Localized id="follow-up-student-results-widget-test-settings-error">
						Something has gone wrong, and we cannot load test settings
					</Localized>
				</Typography>
			</Stack>
			<Button onClick={props.onReload}>
				<Localized id="follow-up-student-results-widget-test-settings-error-action-reload">
					Reload
				</Localized>
			</Button>
		</Stack>
	);
}

function SettingsRow(props: {
	description: React.ReactNode;
	value: React.ReactNode;
	loading?: boolean;
}) {
	if (props.loading) {
		return (
			<Stack direction="row" spacing={1}>
				<Skeleton>
					<Typography variant="body2">{props.description}</Typography>
				</Skeleton>
				<Skeleton>
					<Typography variant="body2">{props.value}</Typography>
				</Skeleton>
			</Stack>
		);
	}

	return (
		<Stack direction="row" spacing={1}>
			<Typography variant="body2">{props.description}</Typography>
			<Typography variant="body2">{props.value}</Typography>
		</Stack>
	);
}

function TestSettingsEditor(props: {
	initialSessionLimit: number;
	initialSessionLimitExtra: number;
	onSave: (s: {sessionLimit: number}) => Promise<boolean>;
	onClose: () => void;
}) {
	const [additionalAttempts, setAdditionalAttempts] = useState(
		props.initialSessionLimitExtra
	);

	const [saving, setSaving] = useState(false);

	const {l10n} = useLocalization();

	async function save() {
		setSaving(true);

		const saved = await props.onSave({
			sessionLimit: additionalAttempts,
		});

		setSaving(false);

		if (saved) {
			props.onClose();
		}
	}

	const dirty = additionalAttempts !== props.initialSessionLimitExtra;

	return (
		<Paper sx={{pt: 2, pr: 2, pb: 1, pl: 4.5, ml: -4.5, mr: -6.25}}>
			<Grid container spacing={1}>
				<Grid size={{xs: 12, sm: 3}}>
					<Typography variant="subtitle2">
						<Localized id="follow-up-student-results-widget-test-settings-editor-title">
							Test settings
						</Localized>
					</Typography>
				</Grid>

				<Grid size={{xs: 12, sm: 9}} container spacing={3}>
					<Grid size={{xs: 12, md: 4}}>
						<TextField
							id="attempts"
							fullWidth
							label={
								<Localized id="follow-up-student-results-widget-test-settings-editor-label-attempts-current">
									Number of attempts
								</Localized>
							}
							value={
								props.initialSessionLimit ||
								l10n.getString(
									"follow-up-student-results-widget-test-settings-attempts-number-no-limit"
								)
							}
							slotProps={{input: {readOnly: true}}}
						/>
					</Grid>
					<Grid size={{xs: 12, md: 4}}>
						<TextField
							id="additional-attempts"
							fullWidth
							autoFocus
							disabled={saving}
							label={
								<Localized id="follow-up-student-results-widget-test-settings-editor-label-attempts-additional">
									Additional attempts
								</Localized>
							}
							type="number"
							slotProps={{inputLabel: {shrink: true}, htmlInput: {min: 0}}}
							value={additionalAttempts}
							onChange={({target}) => {
								const val = parseInt(target.value);
								if (val >= 0) {
									setAdditionalAttempts(val);
								}
							}}
						/>
					</Grid>
					<Grid size={{xs: 12, md: 4}}>
						<TextField
							id="total-attempts"
							fullWidth
							label={
								<Localized id="follow-up-student-results-widget-test-settings-editor-label-attempts-total">
									Total number of attempts
								</Localized>
							}
							value={
								props.initialSessionLimit === 0
									? l10n.getString(
											"follow-up-student-results-widget-test-settings-attempts-number-no-limit"
									  )
									: props.initialSessionLimit + additionalAttempts
							}
							slotProps={{input: {readOnly: true}}}
						/>
					</Grid>
				</Grid>

				<Grid size={12}>
					<Divider sx={{ml: -4.5, mr: -2, mt: 2}} />
				</Grid>

				<Grid size={12} container spacing={1} sx={{justifyContent: "flex-end"}}>
					<Grid>
						<Button color="primary" onClick={props.onClose}>
							<Localized id="follow-up-student-results-widget-test-settings-editor-action-cancel">
								Cancel
							</Localized>
						</Button>
					</Grid>
					<Grid>
						<ProgressButton
							color="primary"
							disabled={!dirty}
							inProgress={saving}
							onClick={save}
						>
							<Localized id="follow-up-student-results-widget-test-settings-editor-action-save">
								Save
							</Localized>
						</ProgressButton>
					</Grid>
				</Grid>
			</Grid>
		</Paper>
	);
}

export default StudentProgressInChapters;
